import React, { useCallback, useState, useEffect, forwardRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import DragItemTypes from "../DragItemTypes";
import { equals, includes, map, pluck } from "ramda";
import {
  useAddToModule,
  useDeleteModuleItems,
} from "../../modules/moduleServices";
import { useRemoveContentFromCollection } from "../../collections/collectionServices";
import { useRemoveCollectionsFromAnthology } from "../../anthologies/anthologyServices";
import { useModuleMedia } from "../../modules/moduleServices";
import { useModuleContentGroups } from "../../creatorServices";
import { useParentDrop } from "../use-parentDrop";
import {
  getContentParentsForAnthology,
  groupChildrenByParent,
} from "../organizeUtil";
import { notBlank } from "../../../util/ext";
import { useSelected, useSidebar } from "../OrganizeProvider";

import TreeItem from "@material-ui/lab/TreeItem";

import CollapseIcon from "./CollapseIcon";
import SidebarModuleChildrenTree from "./SidebarModuleChildrenTree";
import SidebarTreeItemLabel from "./SidebarTreeItemLabel";
import { getIds } from "../../../util/mediaUtil";

const useStyles = makeStyles(theme => ({
  content: {
    backgroundColor: "transparent",
  },

  contentDroppable: {
    backgroundColor: `${theme.palette.primary.main} !important`,
  },

  header: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: "1.6rem",
  },

  label: {
    color: "inherit",
  },

  labelDroppable: {
    color: "white",
  },

  treeItem: {
    fontWeight: "inherit",
  },
}));

const SidebarModuleTreeItem = forwardRef((props, ref) => {
  const {
    module,
    count,
    icon,
    onClick,
    label,
    nodeId,
    nodeType,
    tooltipLabel,
    showAlert,
    ...otherProps
  } = props;

  const classes = useStyles();
  const sidebar = useSidebar();
  const { removeAllSelectedContent, selectedContent } = useSelected();

  const getTypographyProps = type => {
    switch (type) {
      case "root":
        return { classes: { root: classes.header } };
      default:
        return { classes: { root: classes.treeItem } };
    }
  };

  const moduleContentGroups = useModuleContentGroups(
    module.id,
    { limit: "all", sort: "name asc" },
    {
      enabled: includes(nodeId, sidebar.expanded),
    },
  );

  const moduleMedia = useModuleMedia(module.id, {
    enabled: includes(nodeId, sidebar.expanded),
  });
  const [moduleChildren, setModuleChildren] = useState([]);
  useEffect(() => {
    if (moduleContentGroups.isSuccess) {
      setModuleChildren(moduleContentGroups.data.contents);
    }
  }, [moduleContentGroups.isSuccess, moduleContentGroups.data]);

  //-- Handle Drag and Drop Actions
  const addToModule = useAddToModule();
  const deleteModuleItems = useDeleteModuleItems();
  const removeContentFromCollection = useRemoveContentFromCollection();
  const removeCollectionsFromAnthology = useRemoveCollectionsFromAnthology();
  const onDrop = useCallback(
    async ({ items, dropAction, dropItemMediaType }) => {
      try {
        if (equals(dropAction, "move")) {
          const prevParents = groupChildrenByParent(items);

          // Remove children from previous parents
          for (const { prevParent, newChildren } of prevParents) {
            if (notBlank(prevParent.id)) {
              if (equals(prevParent.mediaType, "collection")) {
                await removeContentFromCollection.mutateAsync({
                  collectionId: prevParent.id,
                  contentIds: newChildren.ids,
                });
              }

              if (equals(prevParent.mediaType, "anthology")) {
                if (equals(dropItemMediaType, "content")) {
                  // Remove content from all collections in anthology
                  const collections = getContentParentsForAnthology(
                    selectedContent,
                    prevParent,
                  );

                  await Promise.all(
                    map(
                      collectionId =>
                        removeContentFromCollection.mutateAsync({
                          collectionId,
                          contentIds: getIds(selectedContent),
                        }),
                      getIds(collections),
                    ),
                  );

                  removeAllSelectedContent();
                } else {
                  await removeCollectionsFromAnthology.mutateAsync({
                    anthologyId: prevParent.id,
                    mediaIds: newChildren.ids,
                  });
                }
              }

              if (equals(prevParent.mediaType, "module")) {
                await deleteModuleItems.mutateAsync({
                  id: prevParent.id,
                  ids: newChildren.ids,
                });
              }
            }
          }
        }

        await addToModule.mutateAsync({
          resourceIds: { id: module.id },
          data: { ids: pluck("id", items) },
        });

        for (const item of items) {
          if (notBlank(item.onDropSuccess)) {
            item.onDropSuccess();
          }
        }

        const alert = equals(dropAction, "move")
          ? "Item(s) Successfully Moved"
          : "Item(s) Successfully Added";

        showAlert("success", [alert]);
      } catch (err) {
        console.log(err);
        showAlert("error", [err.message]);
      }
    },
    [
      addToModule,
      deleteModuleItems,
      module,
      selectedContent,
      showAlert,
      removeAllSelectedContent,
      removeContentFromCollection,
      removeCollectionsFromAnthology,
    ],
  );

  const [{ isOver, canDrop }, drop] = useParentDrop({
    parent: module,
    onDrop,
    accept: [
      DragItemTypes.CONTENT,
      DragItemTypes.COLLECTION,
      DragItemTypes.ANTHOLOGY,
    ],
  });

  return (
    <TreeItem
      nodeId={nodeId}
      onClick={onClick}
      classes={{
        content: isOver && canDrop ? classes.contentDroppable : classes.content,
        label: isOver && canDrop ? classes.labelDroppable : classes.label,
      }}
      collapseIcon={<CollapseIcon loading={moduleMedia.isLoading} />}
      label={
        <SidebarTreeItemLabel
          dropRef={drop}
          tooltipLabel={tooltipLabel}
          labelText={label}
          icon={icon}
          TypographyProps={getTypographyProps(nodeType)}
        />
      }
      {...otherProps}
    >
      {notBlank(moduleChildren) ? (
        <SidebarModuleChildrenTree
          module={module}
          moduleChildren={moduleChildren}
          showAlert={showAlert}
        />
      ) : (
        <div />
      )}
    </TreeItem>
  );
});

export default SidebarModuleTreeItem;
